import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'

import { cn } from '../utilitarios/cn'

export interface PropriedadesCaixa extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  rotulo?: ReactNode
  dica?: ReactNode
}

/** Checkbox com rótulo clicável. */
export const Caixa = forwardRef<HTMLInputElement, PropriedadesCaixa>(function Caixa(
  { rotulo, dica, className, ...resto },
  ref,
) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-2.5 text-sm text-conteudo',
        resto.disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      <input
        ref={ref}
        type="checkbox"
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-borda-forte bg-superficie',
          'text-marca-500 accent-marca-500',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marca-500/40',
        )}
        {...resto}
      />
      {(rotulo || dica) && (
        <span className="flex flex-col gap-0.5 leading-tight">
          {rotulo ? <span>{rotulo}</span> : null}
          {dica ? <span className="text-xs text-conteudo-fraco">{dica}</span> : null}
        </span>
      )}
    </label>
  )
})

export interface PropriedadesAlternador {
  marcado: boolean
  onAlterar: (marcado: boolean) => void
  rotulo?: ReactNode
  dica?: ReactNode
  desabilitado?: boolean
  className?: string
}

/**
 * Interruptor para efeito imediato (o toggle de Status na listagem de catálogo,
 * TELA-14). Onde o valor só vale depois de "Salvar", use `Caixa`: um interruptor
 * que não mudou nada até o submit ensina o usuário a duvidar do próprio clique.
 */
export function Alternador({
  marcado,
  onAlterar,
  rotulo,
  dica,
  desabilitado,
  className,
}: PropriedadesAlternador) {
  return (
    <label
      className={cn(
        'flex items-center gap-3 text-sm text-conteudo',
        desabilitado ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={marcado}
        disabled={desabilitado}
        onClick={() => onAlterar(!marcado)}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marca-500/40',
          marcado ? 'bg-marca-500' : 'bg-borda-forte',
          desabilitado && 'cursor-not-allowed',
        )}
      >
        <span
          className={cn(
            'inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform',
            marcado ? 'translate-x-[1.15rem]' : 'translate-x-[0.15rem]',
          )}
        />
      </button>
      {(rotulo || dica) && (
        <span className="flex flex-col leading-tight">
          {rotulo ? <span>{rotulo}</span> : null}
          {dica ? <span className="text-xs text-conteudo-fraco">{dica}</span> : null}
        </span>
      )}
    </label>
  )
}

export interface PropriedadesGrupoRadio<T extends string> {
  nome: string
  valor: T
  onAlterar: (valor: T) => void
  opcoes: ReadonlyArray<{ valor: T; rotulo: string }>
  desabilitado?: boolean
  className?: string
}

/** Radio em linha — o "CPF / CNPJ" da TELA-03. */
export function GrupoRadio<T extends string>({
  nome,
  valor,
  onAlterar,
  opcoes,
  desabilitado,
  className,
}: PropriedadesGrupoRadio<T>) {
  return (
    <div className={cn('flex flex-wrap items-center gap-4', className)} role="radiogroup">
      {opcoes.map((opcao) => (
        <label
          key={opcao.valor}
          className={cn(
            'flex items-center gap-2 text-sm text-conteudo',
            desabilitado ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          )}
        >
          <input
            type="radio"
            name={nome}
            value={opcao.valor}
            checked={valor === opcao.valor}
            disabled={desabilitado}
            onChange={() => onAlterar(opcao.valor)}
            className="h-4 w-4 border-borda-forte accent-marca-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marca-500/40"
          />
          {opcao.rotulo}
        </label>
      ))}
    </div>
  )
}
